/**
 * ValidationRulesEditor — F12.A2 T12.A2.2.
 *
 * Edytor reguł walidacji dla pól modułu (key-value).
 * Backend: PUT `/api/modules/:id/fields/:fieldId/validation-rules` (replace-all).
 *
 * Reguły dostępne per typ pola (whitelist):
 *   - text/textarea: min_length, max_length, pattern (regex)
 *   - email/url: pattern (override default validation)
 *   - phone: pattern, min_length, max_length
 *   - number/decimal/currency: min, max
 *   - date/datetime: min_date, max_date
 *   - file/image: max_size (bytes), allowed_extensions (csv)
 *
 * Renderowany w prawym panelu builder pod opcjami selectów (jeśli applicable do typu).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Shield } from 'lucide-react';

import { Button } from '@/components/Button';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

interface ValidationRulesEditorProps {
  moduleId: string;
  fieldId: string;
  fieldSlug: string;
  fieldType: string;
  initialRules: Record<string, string>;
}

interface RuleSpec {
  name: string;
  labelKey: string;
  inputType: 'number' | 'text' | 'date';
  placeholder?: string;
}

/** Mapowanie field type → dostępne reguły walidacji */
const RULES_BY_TYPE: Record<string, RuleSpec[]> = {
  text: [
    { name: 'min_length', labelKey: 'min_length', inputType: 'number', placeholder: '0' },
    { name: 'max_length', labelKey: 'max_length', inputType: 'number', placeholder: '255' },
    { name: 'pattern', labelKey: 'pattern', inputType: 'text', placeholder: '^[a-zA-Z]+$' },
  ],
  textarea: [
    { name: 'min_length', labelKey: 'min_length', inputType: 'number', placeholder: '0' },
    { name: 'max_length', labelKey: 'max_length', inputType: 'number', placeholder: '5000' },
  ],
  email: [
    {
      name: 'pattern',
      labelKey: 'pattern',
      inputType: 'text',
      placeholder: 'np. ^[^@]+@firma\\.pl$',
    },
  ],
  url: [{ name: 'pattern', labelKey: 'pattern', inputType: 'text', placeholder: 'np. ^https://' }],
  phone: [
    { name: 'pattern', labelKey: 'pattern', inputType: 'text', placeholder: '^\\+48\\s?\\d{9}$' },
    { name: 'min_length', labelKey: 'min_length', inputType: 'number' },
    { name: 'max_length', labelKey: 'max_length', inputType: 'number' },
  ],
  number: [
    { name: 'min', labelKey: 'min', inputType: 'number', placeholder: '0' },
    { name: 'max', labelKey: 'max', inputType: 'number', placeholder: '100' },
  ],
  decimal: [
    { name: 'min', labelKey: 'min', inputType: 'number', placeholder: '0' },
    { name: 'max', labelKey: 'max', inputType: 'number', placeholder: '999.99' },
  ],
  currency: [
    { name: 'min', labelKey: 'min', inputType: 'number', placeholder: '0' },
    { name: 'max', labelKey: 'max', inputType: 'number', placeholder: '100000' },
  ],
  date: [
    { name: 'min_date', labelKey: 'min_date', inputType: 'date' },
    { name: 'max_date', labelKey: 'max_date', inputType: 'date' },
  ],
  datetime: [
    { name: 'min_date', labelKey: 'min_date', inputType: 'date' },
    { name: 'max_date', labelKey: 'max_date', inputType: 'date' },
  ],
  file: [
    { name: 'max_size', labelKey: 'max_size', inputType: 'number', placeholder: '5242880 (5 MB)' },
    {
      name: 'allowed_extensions',
      labelKey: 'allowed_extensions',
      inputType: 'text',
      placeholder: 'pdf,doc,docx',
    },
  ],
  image: [
    { name: 'max_size', labelKey: 'max_size', inputType: 'number', placeholder: '5242880 (5 MB)' },
    {
      name: 'allowed_extensions',
      labelKey: 'allowed_extensions',
      inputType: 'text',
      placeholder: 'jpg,png,gif,webp',
    },
  ],
};

export function ValidationRulesEditor({
  moduleId,
  fieldId,
  fieldSlug,
  fieldType,
  initialRules,
}: ValidationRulesEditorProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const ruleSpecs = useMemo(() => RULES_BY_TYPE[fieldType] ?? [], [fieldType]);
  const [rules, setRules] = useState<Record<string, string>>(initialRules);

  useEffect(() => {
    setRules(initialRules);
  }, [fieldId, initialRules]);

  const hasChanges = useMemo(() => {
    const initialKeys = Object.keys(initialRules).sort();
    const currentKeys = Object.keys(rules)
      .filter((k) => rules[k]?.trim() !== '')
      .sort();
    if (initialKeys.length !== currentKeys.length) return true;
    if (initialKeys.join(',') !== currentKeys.join(',')) return true;
    return currentKeys.some((k) => initialRules[k] !== rules[k]);
  }, [rules, initialRules]);

  const saveMutation = useMutation({
    mutationFn: async (rs: Record<string, string>) => {
      // Wytrim wartości i pomiń puste
      const cleaned: Record<string, string> = {};
      for (const [k, v] of Object.entries(rs)) {
        if (v != null && String(v).trim() !== '') cleaned[k] = String(v).trim();
      }
      const res = await api.put(`/modules/${moduleId}/fields/${fieldId}/validation-rules`, {
        rules: cleaned,
      });
      return res.data;
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.modules.validationRules.toasts.saved') });
      queryClient.invalidateQueries({ queryKey: ['modules', moduleId] });
    },
    onError: () => {
      toast.show({ variant: 'error', title: t('tenant.modules.validationRules.toasts.saveError') });
    },
  });

  if (ruleSpecs.length === 0) {
    return null; // Brak reguł dostępnych dla tego typu pola
  }

  return (
    <div
      className="mt-4 border-t border-border-primary pt-4"
      data-testid={`validation-rules-editor-${fieldSlug}`}
    >
      <div className="mb-3 flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-text-primary">
          <Shield className="h-4 w-4" />
          {t('tenant.modules.validationRules.title')}
        </h4>
        {hasChanges && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => saveMutation.mutate(rules)}
            loading={saveMutation.isPending}
            disabled={saveMutation.isPending}
            data-testid={`validation-rules-editor-${fieldSlug}-save`}
          >
            {t('tenant.modules.validationRules.save')}
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {ruleSpecs.map((spec) => (
          <div key={spec.name}>
            <label
              htmlFor={`vr-${fieldSlug}-${spec.name}`}
              className="mb-1 block text-xs text-text-secondary"
            >
              {t(`tenant.modules.validationRules.rules.${spec.labelKey}`)}
            </label>
            <input
              id={`vr-${fieldSlug}-${spec.name}`}
              type={spec.inputType}
              value={rules[spec.name] ?? ''}
              onChange={(e) =>
                setRules((prev) => {
                  const next = { ...prev };
                  if (e.target.value === '') delete next[spec.name];
                  else next[spec.name] = e.target.value;
                  return next;
                })
              }
              placeholder={spec.placeholder}
              className="w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary placeholder-text-muted focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
              data-testid={`validation-rules-editor-${fieldSlug}-${spec.name}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
